import { useLayoutEffect, useRef, useState } from 'react';

/**
 * 선두 N개 열을 **가로 스크롤에 고정**하기 위한 `left` 오프셋 계산.
 *
 * 열 폭이 내용 기반(`table-layout: auto`)이라 2번째 이후 열의 `left` 를 CSS 로는 못 적는다 — 앞선 열들의
 * **실제 폭 합**이 필요하다. 그래서 헤더 행의 선두 셀들을 재서 누적 오프셋을 만든다(`[0, w0, w0+w1, …]`).
 *
 * **헤더 행을 기준으로 재는 이유** — 열 폭은 표 전체가 공유하므로 한 행만 재면 충분하고, 헤더는 가상화와
 * 무관하게 **항상 마운트**돼 있다(본문 행은 스크롤에 따라 사라진다).
 *
 * ⚠️ 세로 고정(sticky 헤더)과 달리 이건 **높이 상한이 필요 없다** — 기준 스크롤포트가 가로를 소유한
 * 표 자신의 뷰포트라, 표가 자연 높이여도 가로 스크롤은 거기서 일어나기 때문이다.
 */
export function useStickyLeftColumns(count: number) {
  /** 헤더 행 ref — 선두 셀들의 폭 측정 기준. */
  const headerRowRef = useRef<HTMLTableRowElement>(null);
  const [offsets, setOffsets] = useState<number[]>([]);

  useLayoutEffect(() => {
    const row = headerRowRef.current;
    if (count <= 0 || !row) {
      setOffsets((prev) => (prev.length === 0 ? prev : []));
      return;
    }
    const cellsOf = () => Array.from(row.children).slice(0, count) as HTMLElement[];
    const measure = () => {
      let acc = 0;
      const next = cellsOf().map((cell) => {
        const left = acc;
        acc += cell.offsetWidth;
        return left;
      });
      // ⚠️ 값이 같으면 **새 배열을 만들지 않는다** — ResizeObserver 콜백에서 setState 하는 구조라, 매번 새
      // 참조를 넣으면 리렌더 → 재측정 → … 로 돈다.
      setOffsets((prev) =>
        prev.length === next.length && prev.every((v, i) => v === next[i]) ? prev : next,
      );
    };

    measure();
    // 열 폭은 **내용**으로 정해진다 — 데이터가 바뀌면(가상화로 보이는 행 교체 포함) 폭도 바뀐다.
    // 행과 선두 셀 양쪽을 본다: 행은 전체 폭 변화(뷰포트 리사이즈), 셀은 개별 열 폭 변화를 잡는다.
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    for (const cell of cellsOf()) observer.observe(cell);
    return () => observer.disconnect();
  }, [count]);

  return { headerRowRef, offsets };
}
